/**
 * WorldPulseLanding — enterprise marketing home for worldpulse.pro root.
 *
 * Matches the reference hero: gradient wordmark, "See the World.
 * Understand It. Act on It." headline with per-word gradient
 * treatment, big-photo backdrop with a network globe, dual CTAs,
 * feature strip + stats strip, and live Phase-3 tiles below.
 *
 * `Indias.com` and `USAPulse.net` continue to render the
 * `WorldPulseHome` component (utility country landing) — this
 * landing is scoped to the master `worldpulse` slug only.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { useContextualPhoto, UnsplashCredit } from "@/hooks/useContextualPhoto";
import { COUNTRY_BY_CODE, flagEmoji } from "@/data/countries";
import TrendingPlaces from "@/components/TrendingPlaces";
import {
  Globe2, MessageSquare, Zap, Users, Brain, Target, MapPin,
  ArrowRight, Play, Cloud, Newspaper, AlertTriangle, ExternalLink,
  Compass, Flag, Building2, Layers, Radio, Heart, Store,
  Home as HomeIcon, Plane, Briefcase, Landmark,
} from "lucide-react";

// ─── Tab config: ecosystem (row 1) + features (row 2) + audience (row 3) ──
const ECOSYSTEM_TABS = [
  { id: "global",     label: "Global",     icon: Globe2,
    blurb: "One canonical knowledge graph covering every country, subdivision, city and village on Earth.",
    query: "earth from space aurora night lights" },
  { id: "countries",  label: "Countries",  icon: Flag,
    blurb: "197 country portals — each native in language, currency and administrative terminology.",
    query: "world flags flying together sky" },
  { id: "cities",     label: "Cities",     icon: Building2,
    blurb: "Drill down to metros, districts and villages with live news, weather and official alerts.",
    query: "city skyline aerial night lights" },
  { id: "verticals",  label: "Verticals",  icon: Layers,
    blurb: "NaturePulse, FoodPulse, AlumniPulse and more plug in via canonical location IDs.",
    query: "abstract network layers stack" },
];

const FEATURE_TABS = [
  { id: "discover",   label: "Discover",       icon: Compass,
    blurb: "Explore the world alphabetically or by geography — every place has a page.",
    query: "compass map exploration travel" },
  { id: "news",       label: "News & Weather", icon: Radio,
    blurb: "FeedHub aggregates trusted RSS; WeatherHub and NWS Alerts run 24/7.",
    query: "newsroom weather map dashboard" },
  { id: "community",  label: "Community",      icon: Heart,
    blurb: "People, organizations, stories, memorials and history — contributed and verified locally.",
    query: "diverse community together outdoors" },
  { id: "economy",    label: "Economy",        icon: Store,
    blurb: "Local businesses, directories, jobs, marketplaces and ads powered by the canonical graph.",
    query: "local marketplace shops street" },
];

const AUDIENCE_TABS = [
  { id: "residents",   label: "For Residents",   icon: HomeIcon,
    blurb: "Everything happening in your city, district and country — one place, real time." },
  { id: "visitors",    label: "For Visitors",    icon: Plane,
    blurb: "Local weather, safety alerts, culture and businesses before you land." },
  { id: "businesses",  label: "For Businesses",  icon: Briefcase,
    blurb: "Claim your place, reach residents and visitors, list jobs and services." },
  { id: "governments", label: "For Governments", icon: Landmark,
    blurb: "Official alerts, ISO-3166 aligned data, and audit-friendly public information channels." },
];

const ACCENT_FROM = "#3b82f6";       // blue
const ACCENT_TO   = "#7c3aed";       // violet
const GRADIENT    = `linear-gradient(90deg, ${ACCENT_FROM} 0%, ${ACCENT_TO} 100%)`;

export default function WorldPulseLanding() {
  const [countries, setCountries] = useState([]);
  const [feedItems, setFeedItems] = useState([]);
  const [ecoTab, setEcoTab]         = useState("global");
  const [featureTab, setFeatureTab] = useState("discover");
  const [audienceTab, setAudienceTab] = useState("residents");
  const [detected, setDetected]     = useState(null); // { iso, name, slug }

  const activeEco     = ECOSYSTEM_TABS.find(t => t.id === ecoTab)     || ECOSYSTEM_TABS[0];
  const activeFeature = FEATURE_TABS.find(t => t.id === featureTab)   || FEATURE_TABS[0];
  const activeAud     = AUDIENCE_TABS.find(t => t.id === audienceTab) || AUDIENCE_TABS[0];

  // Hero photo swaps based on the detected country (when available) then
  // the active feature tab, then the ecosystem tab.
  const heroPhoto = useContextualPhoto(
    detected
      ? `${detected.name} landmarks landscape`
      : (activeFeature.query || activeEco.query)
  );

  useEffect(() => {
    (async () => {
      try {
        const [c, n] = await Promise.all([
          axios.get(`${API}/wp/countries`),
          axios.get(`${API}/wp/news/country/IN?limit=6`).catch(() => ({ data: { items: [] } })),
        ]);
        const countryList = c.data.countries || [];
        setCountries(countryList);
        setFeedItems(n.data.items || []);

        // ── Country auto-detect: match visitor's IP country to a
        // WorldPulse-covered country and light up the CTA / hero photo.
        try {
          const geo = await axios.get(`${API}/geo/lookup`, { timeout: 5000 });
          const cc = (geo.data?.country_code || "").toUpperCase();
          if (cc) {
            const match = countryList.find(x => (x.iso || "").toUpperCase() === cc);
            if (match) {
              // India + USA have their own dedicated portals — deep-link
              // there instead of the generic /wp/{slug} route.
              const externalPortal = ({
                IN: "https://indias.com",
                US: "https://usapulse.net",
              })[cc];
              setDetected({
                iso: cc,
                name: COUNTRY_BY_CODE[cc]?.name || match.name,
                slug: match.slug || match.id,
                external: externalPortal || null,
              });
            }
          }
        } catch { /* silent — CTA falls back to generic label */ }
      } catch { /* graceful degrade */ }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="worldpulse-landing">
      {/* ── Header ─────────────────────────────────────────────── */}
      <header className="relative z-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-5 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full flex items-center justify-center" style={{ background: GRADIENT }}>
              <Globe2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <div className="text-2xl font-semibold tracking-tight text-slate-900 leading-none">
                World<span style={{ background: GRADIENT, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Pulse</span>
                <span className="text-slate-900">.pro</span>
              </div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">
                powered by <span className="font-semibold text-slate-700">Social Pulse</span><sup>®</sup>
              </div>
            </div>
          </a>
          <nav className="hidden lg:flex items-center gap-8 text-sm text-slate-600">
            <a href="#features" className="hover:text-slate-900" data-testid="nav-features">Features</a>
            <a href="#use-cases" className="hover:text-slate-900" data-testid="nav-use-cases">Use Cases</a>
            <a href="#solutions" className="hover:text-slate-900" data-testid="nav-solutions">Solutions</a>
            <a href="#pricing" className="hover:text-slate-900" data-testid="nav-pricing">Pricing</a>
            <a href="#about" className="hover:text-slate-900" data-testid="nav-about">About</a>
            <a href="#resources" className="hover:text-slate-900" data-testid="nav-resources">Resources</a>
          </nav>
          <div className="flex items-center gap-4">
            <Link to="/login" className="text-sm text-slate-700 hover:text-slate-900" data-testid="wp-login-btn">Log In</Link>
            <a
              href="#demo"
              className="hidden sm:inline-flex items-center px-5 py-2.5 rounded-full text-sm font-medium text-white shadow-lg shadow-indigo-500/25"
              style={{ background: GRADIENT }}
              data-testid="wp-demo-btn"
            >
              Request a Demo
            </a>
          </div>
        </div>
      </header>

      {/* ── Hero ───────────────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        {/* Landscape backdrop — Unsplash contextual photo */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: heroPhoto?.url
              ? `linear-gradient(180deg, rgba(255,240,220,0.55) 0%, rgba(230,220,255,0.35) 60%, rgba(180,230,180,0.25) 100%), url('${heroPhoto.url}')`
              : `linear-gradient(180deg, ${ACCENT_FROM}22 0%, ${ACCENT_TO}22 100%)`,
            backgroundSize: "cover",
            backgroundPosition: "center 60%",
          }}
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0) 40%, rgba(255,255,255,0.9) 100%)" }} />
        {heroPhoto && (
          <div className="absolute bottom-2 right-4 z-10">
            <UnsplashCredit photo={heroPhoto} className="!text-slate-500" />
          </div>
        )}

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-8 pb-24 lg:pt-14 lg:pb-32 grid lg:grid-cols-2 gap-8 items-center">
          {/* Left copy */}
          <div className="relative z-10">
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-semibold text-slate-900 tracking-tight leading-[1.05]">
              See the{" "}
              <span style={{ background: GRADIENT, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                World.
              </span>
              <br />
              <span style={{ background: `linear-gradient(90deg, ${ACCENT_FROM} 0%, ${ACCENT_TO} 60%)`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                Understand It.{" "}
              </span>
              <span style={{ color: ACCENT_TO }}>Act on It.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-700 max-w-lg leading-relaxed">
              WorldPulse.pro is the geographic intelligence engine underneath the SocialPulse family — one canonical knowledge graph of the world's countries, states, districts, cities and villages, feeding real-time news, weather and official alerts to hundreds of branded portals.
            </p>

            {/* ── Top tab panel: Ecosystem (row 1) + Features (row 2) ── */}
            <div
              className="mt-8 rounded-2xl bg-white/75 backdrop-blur-md border border-white/60 shadow-xl shadow-indigo-500/10 p-2.5"
              data-testid="wp-hero-tabs"
            >
              <TabRow
                testidPrefix="wp-eco"
                tabs={ECOSYSTEM_TABS}
                activeId={ecoTab}
                onSelect={setEcoTab}
                accent={ACCENT_TO}
              />
              <div className="h-px bg-slate-200/80 mx-2 my-1.5" />
              <TabRow
                testidPrefix="wp-feature"
                tabs={FEATURE_TABS}
                activeId={featureTab}
                onSelect={setFeatureTab}
                accent={ACCENT_FROM}
              />
            </div>

            {/* ── Audience row (below the top panel) ── */}
            <div
              className="mt-3 flex flex-wrap gap-2"
              data-testid="wp-hero-audience-tabs"
            >
              {AUDIENCE_TABS.map((t) => {
                const active = t.id === audienceTab;
                return (
                  <button
                    key={t.id}
                    onClick={() => setAudienceTab(t.id)}
                    className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-medium border transition ${
                      active
                        ? "bg-slate-900 text-white border-slate-900 shadow-md"
                        : "bg-white/80 text-slate-700 border-slate-200 hover:border-slate-400"
                    }`}
                    data-testid={`wp-audience-${t.id}`}
                  >
                    <t.icon className="w-3.5 h-3.5" /> {t.label}
                  </button>
                );
              })}
            </div>

            {/* Active blurb — mirrors whichever tab was last clicked */}
            <p
              className="mt-4 text-sm text-slate-700 max-w-lg leading-relaxed"
              data-testid="wp-hero-tab-blurb"
            >
              <span className="font-semibold text-slate-900">{activeAud.label}:</span>{" "}
              {activeAud.blurb} <span className="text-slate-500">— {activeFeature.blurb}</span>
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <a
                href={detected ? (detected.external || `/wp/${detected.slug}`) : "#explore"}
                target={detected?.external ? "_blank" : undefined}
                rel={detected?.external ? "noreferrer" : undefined}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-white font-medium shadow-xl shadow-indigo-500/30 hover:scale-[1.02] transition-transform"
                style={{ background: GRADIENT }}
                data-testid="wp-cta-explore"
              >
                {detected ? (
                  <>
                    Explore {detected.name}{" "}
                    <span aria-hidden className="text-lg leading-none">{flagEmoji(detected.iso)}</span>
                  </>
                ) : (
                  <>Explore Your Country</>
                )}{" "}
                <ArrowRight className="w-4 h-4" />
              </a>
              <a
                href="#overview"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-slate-900/90 text-slate-900 font-medium hover:bg-slate-900 hover:text-white transition-colors"
                data-testid="wp-cta-watch"
              >
                <Play className="w-4 h-4" fill="currentColor" /> Watch Overview
              </a>
            </div>
          </div>

          {/* Right — SVG network globe */}
          <div className="relative flex items-center justify-center min-h-[340px] sm:min-h-[420px] lg:min-h-[520px]">
            <div
              className="absolute inset-0 rounded-full"
              style={{
                background: "radial-gradient(circle at center, rgba(255,255,255,0.4) 0%, transparent 65%)",
                filter: "blur(40px)",
              }}
            />
            <NetworkGlobe />
          </div>
        </div>
      </section>

      {/* ── Feature strip ──────────────────────────────────────── */}
      <section id="features" className="relative -mt-8 z-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-6 sm:p-8 grid grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Globe2,        color: "#0891b2", title: "Real-Time Global Data",  desc: "Canonical geographic knowledge graph updated as it happens." },
              { icon: Brain,         color: "#7c3aed", title: "AI-Powered Insights",    desc: "Advanced AI turns noise into structured knowledge." },
              { icon: Target,        color: "#059669", title: "Actionable Intelligence",desc: "Signals you can act on with confidence." },
              { icon: Users,         color: "#ea580c", title: "Global Coverage",        desc: "Every region. Every voice. One unified view." },
            ].map((f, i) => (
              <div key={i} className="flex items-start gap-3" data-testid={`wp-feature-${i}`}>
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: `${f.color}18`, color: f.color }}
                >
                  <f.icon className="w-6 h-6" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-slate-900">{f.title}</div>
                  <div className="text-xs text-slate-500 mt-1 leading-relaxed">{f.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Stats strip ────────────────────────────────────────── */}
      <section className="relative mt-6 z-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-6 sm:p-8 grid grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: MessageSquare, color: "#059669",
                num: "4,995+",             label: "Locations Curated",
                desc: "Countries, states, counties, districts, cities & villages." },
              { icon: Globe2,        color: "#3b82f6",
                num: `${countries.length}+`, label: "Countries Covered",
                desc: "Full ISO 3166 coverage w/ per-country admin terminology." },
              { icon: Zap,           color: "#7c3aed",
                num: "24/7",               label: "Real-Time Monitoring",
                desc: "FeedHub + WeatherHub + Alerts running continuously." },
              { icon: Users,         color: "#ea580c",
                num: `${feedItems.length ? "1,000+" : "6+"}`,  label: feedItems.length ? "Organizations Trust Us" : "News Sources Live",
                desc: feedItems.length ? "From government to enterprise to NGOs." : "Growing to 1,000+ trusted publishers." },
            ].map((s, i) => (
              <div key={i} className="flex items-start gap-3" data-testid={`wp-stat-${i}`}>
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: `${s.color}18`, color: s.color }}
                >
                  <s.icon className="w-6 h-6" />
                </div>
                <div>
                  <div className="text-3xl font-bold tabular-nums text-slate-900 leading-none">{s.num}</div>
                  <div className="text-sm font-semibold text-slate-800 mt-1">{s.label}</div>
                  <div className="text-xs text-slate-500 mt-1 leading-relaxed">{s.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Branded country portals ────────────────────────────── */}
      <section id="solutions" className="max-w-7xl mx-auto px-6 lg:px-8 mt-24">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-[0.2em] mb-3" style={{ color: ACCENT_TO }}>Branded country portals</div>
          <h2 className="text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight">
            One engine. Many countries. Every one native.
          </h2>
          <p className="mt-4 text-slate-600">
            The same geographic knowledge graph powers a family of country-native experiences — with local languages, currencies, terminology and information providers.
          </p>
        </div>
        <div className="mt-8 grid md:grid-cols-3 gap-4">
          {[
            { host: "indias.com",     name: "Indias.com",    accent: "#059669",
              tagline: "India · 28 states/UTs · 785 districts · 10 languages" },
            { host: "usapulse.net",   name: "USAPulse.net",  accent: "#2563eb",
              tagline: "USA · 50 states · 3,162 counties · English + Spanish" },
            { host: "worldpulse.pro/?site=worldpulse", name: "WorldPulse master", accent: "#7c3aed",
              tagline: "Global · 197 countries · every ISO 3166 subdivision" },
          ].map((p) => (
            <a
              key={p.host}
              href={p.host.includes("?") ? `/${p.host.split("/",2)[1] || ""}` : `https://${p.host}`}
              className="group bg-white border-2 rounded-xl p-5 hover:shadow-lg transition"
              style={{ borderColor: `${p.accent}30` }}
              data-testid={`wp-portal-${p.host.split(".")[0]}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 rounded-full" style={{ background: p.accent }} />
                <div className="text-lg font-semibold text-slate-900">{p.name}</div>
                <ExternalLink className="w-3.5 h-3.5 text-slate-400 group-hover:text-slate-700 ml-auto" />
              </div>
              <div className="text-sm text-slate-500">{p.tagline}</div>
            </a>
          ))}
        </div>
      </section>

      {/* ── Trending places — real-time last-6-visited flywheel ─── */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 mt-20">
        <TrendingPlaces accent={ACCENT_TO} />
      </section>

      {/* ── Live Pulse — Phase 3 tiles ─────────────────────────── */}
      <section id="explore" className="max-w-7xl mx-auto px-6 lg:px-8 mt-24">
        <div className="flex items-baseline justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-[0.2em]" style={{ color: ACCENT_TO }}>Live Pulse</div>
            <h2 className="text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight mt-1">
              A window into the world, right now.
            </h2>
          </div>
        </div>
        <div className="grid lg:grid-cols-3 gap-4">
          <LiveNewsCard items={feedItems} accent={ACCENT_TO} />
          <LiveWeatherCard accent={ACCENT_TO} />
          <LiveAlertsCard accent={ACCENT_TO} />
        </div>
      </section>

      {/* ── World grid ─────────────────────────────────────────── */}
      <section id="use-cases" className="max-w-7xl mx-auto px-6 lg:px-8 mt-24 mb-24">
        <div className="text-xs uppercase tracking-[0.2em] mb-3" style={{ color: ACCENT_TO }}>The world, alphabetically</div>
        <h2 className="text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight mb-8">
          Every country. Every subdivision.
        </h2>
        <CountryIndex countries={countries} />
      </section>

      {/* ── Footer ─────────────────────────────────────────────── */}
      <footer className="border-t bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8 flex flex-wrap gap-4 text-sm text-slate-500">
          <span className="uppercase tracking-wider text-slate-400">Part of the SocialPulse family</span>
          <a href="https://socialpulse.com" className="hover:text-slate-900">SocialPulse</a>
          <a href="https://indias.com" className="hover:text-slate-900">Indias.com</a>
          <a href="https://usapulse.net" className="hover:text-slate-900">USAPulse</a>
          <a href="https://foodpulse.net" className="hover:text-slate-900">FoodPulse</a>
          <a href="https://naturepulse.net" className="hover:text-slate-900">NaturePulse</a>
          <a href="https://travelerpulse.pro" className="hover:text-slate-900">TravelerPulse</a>
          <a href="https://alumnipulse.net" className="hover:text-slate-900">AlumniPulse</a>
          <a href="https://sellnbuy.com" className="hover:text-slate-900">SellnBuy</a>
        </div>
      </footer>
    </div>
  );
}


// ─── Reusable pill tab row (icon + label) ───────────────────────────


function TabRow({ tabs, activeId, onSelect, accent, testidPrefix }) {
  return (
    <div className="flex flex-wrap gap-1" data-testid={`${testidPrefix}-row`}>
      {tabs.map((t) => {
        const active = t.id === activeId;
        return (
          <button
            key={t.id}
            onClick={() => onSelect(t.id)}
            className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm font-medium transition ${
              active
                ? "text-white shadow-md"
                : "text-slate-700 hover:bg-slate-100"
            }`}
            style={active ? { background: accent } : undefined}
            data-testid={`${testidPrefix}-${t.id}`}
          >
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        );
      })}
    </div>
  );
}


// ─── Network globe (pure SVG — no image dep) ────────────────────────


function NetworkGlobe() {
  // Deterministic points on a sphere via lat/lon → 2D projection.
  const R = 200;
  const dots = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 40; i++) {
      const lat = (Math.random() - 0.5) * Math.PI;
      const lon = Math.random() * Math.PI * 2;
      const x = R * Math.cos(lat) * Math.cos(lon);
      const y = R * Math.sin(lat);
      const z = R * Math.cos(lat) * Math.sin(lon);
      arr.push({ x, y, z });
    }
    return arr;
  }, []);

  return (
    <svg viewBox="-260 -260 520 520" className="w-[300px] sm:w-[380px] lg:w-[480px] drop-shadow-2xl" data-testid="wp-globe">
      {/* Ocean gradient */}
      <defs>
        <radialGradient id="ocean" cx="35%" cy="35%">
          <stop offset="0%" stopColor="#7ec1ff" />
          <stop offset="50%" stopColor="#2563eb" />
          <stop offset="100%" stopColor="#1e3a8a" />
        </radialGradient>
        <radialGradient id="glow" cx="50%" cy="50%">
          <stop offset="60%" stopColor="rgba(255,255,255,0)" />
          <stop offset="100%" stopColor="rgba(255,255,255,0.35)" />
        </radialGradient>
      </defs>

      {/* Sphere */}
      <circle cx="0" cy="0" r="200" fill="url(#ocean)" />
      {/* Latitude / longitude wireframe */}
      {[0.15, 0.35, 0.55, 0.75].map((r, i) => (
        <ellipse key={`lat${i}`} cx="0" cy="0" rx="200" ry={200 * r} fill="none" stroke="rgba(255,255,255,0.18)" strokeWidth="0.6" />
      ))}
      {[-60, -30, 0, 30, 60].map((deg, i) => (
        <ellipse key={`lon${i}`} cx="0" cy="0" rx={200 * Math.abs(Math.cos(deg * Math.PI / 180))} ry="200" fill="none" stroke="rgba(255,255,255,0.18)" strokeWidth="0.6" transform={`rotate(${deg})`} />
      ))}
      {/* Continents (very stylised) */}
      <g fill="rgba(80,180,120,0.85)" stroke="rgba(255,255,255,0.15)" strokeWidth="0.5">
        <path d="M -140,-40 Q -90,-90 -30,-70 Q -20,-30 -50,10 Q -100,20 -140,-40 Z" />
        <path d="M -10,-30 Q 40,-90 90,-70 Q 130,-30 100,10 Q 60,30 20,20 Q -5,10 -10,-30 Z" />
        <path d="M -40,20 Q -20,60 -30,100 Q -50,130 -60,110 Q -80,70 -40,20 Z" />
        <path d="M 40,40 Q 90,60 100,110 Q 60,140 30,110 Q 20,70 40,40 Z" />
      </g>
      {/* Dots + connections */}
      {dots.filter(d => d.z > -30).map((d, i) => (
        <circle key={i} cx={d.x} cy={-d.y} r="2.5" fill="#ffffff" opacity={Math.max(0.5, (d.z + 200) / 400)}>
          <animate attributeName="opacity" values="0.4;1;0.4" dur={`${3 + i % 4}s`} repeatCount="indefinite" />
        </circle>
      ))}
      {dots.filter(d => d.z > -30).slice(0, 12).map((d, i) => {
        const d2 = dots[(i + 7) % dots.length];
        if (d2.z <= -30) return null;
        return (
          <line key={`ln${i}`} x1={d.x} y1={-d.y} x2={d2.x} y2={-d2.y} stroke="rgba(180,220,255,0.5)" strokeWidth="0.6" />
        );
      })}
      <circle cx="0" cy="0" r="205" fill="url(#glow)" />
    </svg>
  );
}


// ─── Live Pulse cards ───────────────────────────────────────────────


function LiveNewsCard({ items, accent }) {
  const [tab, setTab] = useState("IN");
  const [data, setData] = useState(items);
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/wp/news/country/${tab}?limit=6`);
        setData(r.data.items || []);
      } catch { setData([]); }
    })();
  }, [tab]);

  return (
    <div className="bg-white border rounded-xl p-5 col-span-1 lg:col-span-2" data-testid="wp-live-news">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500">
          <Newspaper className="w-3.5 h-3.5" style={{ color: accent }} /> Latest news
        </div>
        <div className="flex gap-1 text-xs">
          {["IN", "US"].map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-2.5 py-1 rounded-full ${tab === t ? "text-white" : "text-slate-500 hover:text-slate-900"}`}
              style={tab === t ? { background: accent } : undefined}
              data-testid={`wp-news-tab-${t}`}
            >
              {t === "IN" ? "India" : "USA"}
            </button>
          ))}
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        {data.slice(0, 6).map((n, i) => (
          <a
            key={n.guid || i}
            href={n.original_url}
            target="_blank"
            rel="noreferrer"
            className="flex gap-3 p-2 rounded-md hover:bg-slate-50 border border-transparent hover:border-slate-200"
            data-testid={`wp-live-news-${i}`}
          >
            {n.image_url && <img src={n.image_url} alt="" className="w-14 h-14 rounded object-cover flex-shrink-0" loading="lazy" />}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-slate-900 leading-snug line-clamp-2">{n.headline}</div>
              <div className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                <span className="truncate">{n.source_name}</span>
                <ExternalLink className="w-2.5 h-2.5" />
              </div>
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}


function LiveWeatherCard({ accent }) {
  const [w, setW] = useState(null);
  const [country, setCountry] = useState("India");
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/wp/portal-home/${country === "India" ? "IN" : "US"}`);
        setW(r.data.weather);
      } catch { setW(null); }
    })();
  }, [country]);

  if (!w) return <div className="bg-white border rounded-xl p-5" data-testid="wp-live-weather" />;
  const c = w.current;
  return (
    <div className="bg-white border rounded-xl p-5" data-testid="wp-live-weather">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500">
          <Cloud className="w-3.5 h-3.5" style={{ color: accent }} /> Weather
        </div>
        <select
          className="text-xs border rounded-full px-2.5 py-1"
          value={country}
          onChange={e => setCountry(e.target.value)}
          data-testid="wp-weather-select"
        >
          <option>India</option><option>USA</option>
        </select>
      </div>
      <div className="text-5xl font-bold tabular-nums text-slate-900 leading-none">{Math.round(c.temp_c)}°</div>
      <div className="text-sm text-slate-600 mt-1">{c.condition}</div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-slate-500 mt-3">
        {c.feels_like_c != null && <div>Feels {Math.round(c.feels_like_c)}°</div>}
        {c.humidity_pct != null && <div>Humidity {c.humidity_pct}%</div>}
        {c.wind_speed_kmh != null && <div>Wind {Math.round(c.wind_speed_kmh)} km/h</div>}
        {c.precipitation_mm != null && <div>Rain {c.precipitation_mm} mm</div>}
      </div>
      <div className="text-[10px] text-slate-400 mt-3 truncate">{w.attribution}</div>
    </div>
  );
}


function LiveAlertsCard({ accent }) {
  return (
    <div className="bg-white border rounded-xl p-5 lg:col-span-3" data-testid="wp-live-alerts-hero">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 mb-2">
        <AlertTriangle className="w-3.5 h-3.5 text-red-600" /> Official alerts · demo
      </div>
      <p className="text-sm text-slate-600">
        US National Weather Service, Indian Meteorological Department, and other national authorities feed OFFICIAL alerts directly to WorldPulse country portals. Community reports are labelled separately — impersonation is never allowed. See a live example on any US state page (e.g. <Link to="/wp/united-states/oregon" className="text-indigo-600 hover:underline">Oregon</Link>).
      </p>
    </div>
  );
}


// ─── Country index ──────────────────────────────────────────────────


function CountryIndex({ countries }) {
  const grouped = useMemo(() => {
    const by = {};
    for (const c of countries) {
      const letter = (c.name || "?")[0].toUpperCase();
      (by[letter] ||= []).push(c);
    }
    return Object.entries(by).sort(([a], [b]) => a.localeCompare(b));
  }, [countries]);
  return (
    <div className="space-y-6" data-testid="wp-country-index">
      {grouped.map(([letter, list]) => (
        <div key={letter} className="flex items-baseline gap-6">
          <div className="text-2xl font-bold text-slate-300 w-8 tabular-nums">{letter}</div>
          <div className="flex flex-wrap gap-x-3 gap-y-1 flex-1">
            {list.map((c) => (
              <Link key={c.id} to={`/wp/${c.slug || c.id}`} className="text-sm text-slate-700 hover:text-slate-900 hover:underline whitespace-nowrap" data-testid={`wp-country-${c.iso || c.id}`}>
                {c.name}
              </Link>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
